import {NumberSpinner} from 'components/customer/Product/QuickAdd/NumberSpinner';
import {Button} from 'components/customer/Product/QuickAdd/Overlay';
import {useProductContext} from 'contexts';
import {RECENTLY_ADDED_KEYS} from 'hooks';
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {mobileLandscape, mobilePortrait} from 'shared/Breakpoints';
import {useLinkFormatter} from 'shared/components/Product/helpers/useItem';
import {ItemType} from 'shared/components/Product/Item';
import {useComponentMountedHelper} from 'shared/helpers';
import Icon from 'shared/Icon';
import styled, {css} from 'styled-components';

interface HardwareControlsProps {
    isProduct: boolean;
    item: ItemType;
    setError?: (errors: string[]) => void;
}

export const HardwareControls = ({
    isProduct,
    item,
    setError,
}: HardwareControlsProps) => {
    const navigate = useNavigate();
    const [count, setCount] = useState(1);
    const [cartButtonDisabled, setCartButtonDisabled] = useState(false);

    const {AddToCart: addToCart, addRecentItem} = useProductContext();
    const {isMounted} = useComponentMountedHelper();
    const {getLink} = useLinkFormatter(item, isProduct);

    const addToCartHandler = useCallback(() => {
        if (setError && count < 1) {
            setError(['Quantity must be greater than 0']);
            return;
        }
        setCartButtonDisabled(true);
        if (addToCart && addRecentItem) {
            addToCart(item, count)
                .then(() => {
                    addRecentItem(item.id, RECENTLY_ADDED_KEYS.HARDWARE);
                    isMounted.current && setCartButtonDisabled(false);
                })
                .catch(() => {
                    // handle stuffs here
                });
        }
    }, [addToCart, addRecentItem, setError, count, item, isMounted]);

    useEffect(() => {
        if (setError && count > 0) {
            setError([]);
        }
    }, [count]);

    useEffect(() => {
        setCartButtonDisabled(false);
        if (item.hasOwnProperty('stockStatus') && item.stockStatus == 1) {
            setCartButtonDisabled(true);
        }
    }, [item]);

    const onClickHandler = useCallback(() => {
        void navigate(getLink());
    }, [navigate, getLink]);

    if (isProduct) return null;

    return (
        <Container>
            {item.hasOwnProperty('advanced') && item.advanced ? (
                <HardwareButton
                    disabled={cartButtonDisabled}
                    onClick={onClickHandler}>
                    <Icon iconName="Button-Add.svg" />
                    <label>Click for More Options</label>
                </HardwareButton>
            ) : (
                <>
                    <NumberSpinner
                        quantity={count}
                        setQuantity={setCount}
                        item={item}
                    />
                    <HardwareButton
                        disabled={cartButtonDisabled}
                        onClick={addToCartHandler}>
                        <Icon iconName="Button-Add.svg" />
                        <label>Add to Cart</label>
                    </HardwareButton>
                </>
            )}
        </Container>
    );
};

const HardwareButton = styled.button`
    ${Button}
    background: ${({theme}) => theme.colors.secondary?.main};
    border: 0;

    &:hover,
    &:focus,
    &:active {
        background: ${({theme}) => theme.colors.primary?.main};
    }
    flex-basis: 80%;

    > label {
        color: white;
    }

    > img {
        margin-right: 0;
    }
`;

const MobileStyle = css`
    flex-direction: column;

    > button {
        width: 100%;
    }
`;

const Container = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;

    ${mobilePortrait`${MobileStyle}`}
    ${mobileLandscape`${MobileStyle}`}
`;
